/**
 * Height conversion utilities for cm <-> ft/in
 */

export interface HeightInCm {
  value: number;
  unit: "cm";
}

export interface HeightInFtIn {
  feet: number;
  inches: number;
  unit: "ft/in";
}

/**
 * Convert centimeters to feet and inches
 * @param cm - Height in centimeters
 * @returns Object with feet and inches
 */
export function cmToFeetInches(cm: number): { feet: number; inches: number } {
  const totalInches = cm / 2.54;
  const feet = Math.floor(totalInches / 12);
  const inches = Math.round(totalInches % 12);
  return { feet, inches };
}

/**
 * Convert feet and inches to centimeters
 * @param feet - Height in feet
 * @param inches - Height in inches
 * @returns Height in centimeters (rounded)
 */
export function feetInchesToCm(feet: number, inches: number): number {
  const totalInches = feet * 12 + inches;
  return Math.round(totalInches * 2.54);
}

/**
 * Format height for display based on unit
 * @param value - Height value (cm or "ft'in" format)
 * @param unit - Unit type ('cm' or 'ft/in')
 * @returns Formatted string for display
 */
export function formatHeight(value: string, unit: string): string {
  if (unit === "cm") {
    return `${value} cm`;
  }
  // ft/in format: "5'7"
  return value;
}

/**
 * Parse height string to extract numeric values
 * @param heightStr - Height string (e.g., "5'7" or "170")
 * @param unit - Unit type
 * @returns Parsed height values
 */
export function parseHeight(
  heightStr: string,
  unit: string
): { feet?: string; inches?: string; cm?: string } {
  if (unit === "cm") {
    return { cm: heightStr };
  }
  // Parse ft/in format: "5'7"
  const match = heightStr.match(/^(\d+)'?(\d*)$/);
  if (match) {
    return {
      feet: match[1] || "",
      inches: match[2] || "",
    };
  }
  return { feet: "", inches: "" };
}
